import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import { ProviderType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { logError } from 'lib/logging'

const SCHEDULING_AVAILABILITY_QUERY = graphql(`
  query SchedulingAvailability($locationId: ID!) {
    location(id: $locationId) {
      id
      availabilityMode
      kollaConsumerId
    }
    providers(pagination: { limit: 200 }) {
      hits {
        id
        displayName
        resourceName
        vendorId
        deactivated
        kollaConsumerId
        hasProfilePicture
        priority
        isPrimary
        pmsDisplayName
        pmsShortDisplayName
        taxonomy
        workspaceId
        orgId
      }
    }
    appointmentTypes(locationId: $locationId, pagination: { limit: 200 }) {
      hits {
        id
        name
        displayName
        vendorId
        appointmentLength
        activeScheduler
        patientType
      }
    }
  }
`)

export type UseSchedulingAvailabilityReturn = {
  user: ReturnType<typeof useUser>
  isSyncing: boolean
  isSyncingSchedules: boolean
  rows: ProviderType[]
  view: `all` | `active` | `inactive`
  activeProviders: ProviderType[]
  inactiveProviders: ProviderType[]
  bookableAppointmentTypes: any
  operatories: KollaProvidersEntityOrResourcesEntity[] | undefined
  operatoriesError: any
  operatoriesSlow: boolean
  isDentrix: boolean
  standardMode: boolean
  onRefreshClick: () => Promise<void>
  onSyncSchedulesClick: () => Promise<void>
  onViewChange: (view: `all` | `active` | `inactive`) => void
  handleProviderStatusChange: (provider: ProviderType, active: boolean) => void
}

export function useSchedulingAvailability(): UseSchedulingAvailabilityReturn {
  const user = useUser()
  const { locationId } = useParams()
  const [bookableAppointmentTypes, setBookableAppointmentTypes] = useState<any>()
  const [operatories, setOperatories] = useState<KollaProvidersEntityOrResourcesEntity[] | undefined>()
  const [operatoriesError, setOperatoriesError] = useState<any>()
  const [operatoriesSlow, setOperatoriesSlow] = useState(false)
  const [isDentrix, setIsDentrix] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSyncingSchedules, setIsSyncingSchedules] = useState(false)
  const [rows, setRows] = useState<ProviderType[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<`all` | `active` | `inactive`>(
    (searchParams.get(`view`) as `all` | `active` | `inactive`) || `active`,
  )

  const [result] = useQuery({
    query: SCHEDULING_AVAILABILITY_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })

  useEffect(() => {
    if (result.data?.providers?.hits) {
      setRows(result.data.providers.hits as ProviderType[])
    }
    if (result.data?.appointmentTypes?.hits) {
      setBookableAppointmentTypes(result.data.appointmentTypes.hits)
    }
  }, [result.data])

  // Fetch PMS-specific data (operatories, isDentrix) via REST — no GraphQL equivalent
  useEffect(() => {
    if (!locationId) return
    fetch(`/api/scheduling/locations/${locationId}/scheduling-data`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => {
        setOperatories(data.operatories)
        setIsDentrix(data.isDentrix ?? false)
      })
      .catch((err) => logError(`Error fetching scheduling availability`, err))
  }, [locationId])

  const activeProviders = useMemo(() => rows?.filter((provider) => provider.deactivated !== true), [rows])
  const inactiveProviders = rows?.filter((provider) => provider.deactivated === true)

  // Standard mode (Kolla locations) sources hours and operatories from the PMS, so an
  // availability can be saved with only a provider and appointment types. Mirrors the API's
  // loadLocationAvailabilities check.
  const location = result.data?.location
  const standardMode = location?.availabilityMode === `standard` && !!location?.kollaConsumerId

  // Sync providers — REST-only (no GraphQL mutation available)
  const onRefreshClick = async () => {
    setIsSyncing(true)
    fetch(`/api/scheduling/locations/${locationId}/scheduling-data`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `providers` }),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsSyncing(false)
        if (data?.success && data.providers) {
          if (data.providers.length > 0) {
            setRows((prev) => {
              const existingIds = new Set(prev.map((p) => p.id))
              const actuallyNew = (data.providers as ProviderType[]).filter((p) => !existingIds.has(p.id))
              const newProviderIds = new Set((data.providers as ProviderType[]).map((p) => p.id))
              const existingProviders = prev.filter((p) => !newProviderIds.has(p.id))
              const combined = [...(data.providers as ProviderType[]), ...existingProviders]
              notifySuccess(actuallyNew.length ? `${actuallyNew.length} New Providers Added` : `No New Providers Added`)
              return combined.sort((a, b) => (a.displayName || ``).localeCompare(b.displayName || ``))
            })
          } else {
            notifySuccess(`No New Providers Added`)
          }
          onViewChange(`all`)
        } else {
          notifyError(`Error syncing providers`)
        }
      })
  }

  // Sync schedules — REST-only (no GraphQL mutation available)
  const onSyncSchedulesClick = async () => {
    setIsSyncingSchedules(true)
    fetch(`/api/scheduling/locations/${locationId}/scheduling-data`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `schedules` }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          notifySuccess(`PMS Schedules Synced`)
        } else {
          notifyError(`Error syncing schedules`)
        }
        setIsSyncingSchedules(false)
      })
  }

  const onViewChange = (view: `all` | `active` | `inactive`) => {
    setView(view)
    setSearchParams({ view })
  }

  const handleProviderStatusChange = (provider: ProviderType, active: boolean) => {
    setRows((prev) => prev.map((p) => (p.id === provider.id ? { ...p, deactivated: !active } : p)))
  }

  return {
    user,
    isSyncing,
    isSyncingSchedules,
    rows,
    view,
    activeProviders,
    inactiveProviders,
    bookableAppointmentTypes,
    operatories: operatories as KollaProvidersEntityOrResourcesEntity[] | undefined,
    operatoriesError,
    operatoriesSlow,
    isDentrix,
    standardMode,
    onRefreshClick,
    onSyncSchedulesClick,
    onViewChange,
    handleProviderStatusChange,
  }
}
